import { serializeQuery } from "@/utils/UrlUtils";
import { IgnoredErrors } from "@/utils/constants/ErrorConstants";
import { PATH_CUSTOM_METRIC_PIECHART } from "@/utils/constants/CustomMetricsConstants";
import { apiFetch, ApiBaseUrl } from "./fetch";

// const SEGMENT_CUSTOM_METRIC_TYPES = [];

export default class ApiMetrics {
  static getOneSegment(
    appId,
    segmentId,
    params = {
      metric_periods: [{ period: "last_seven_to_fourteen_days" }, { period: "last_seven_days" }],
    },
  ) {
    let query = serializeQuery(params);
    if (query) query = `?${query}`;
    return apiFetch(`${ApiBaseUrl}apps/${appId}/segments/${segmentId}/metrics${query}`);
  }

  // static getOneSegmentCustom(
  //   appId,
  //   segmentId,
  //   params = {
  //     metric_periods: [{ period: "last_seven_to_fourteen_days" }, { period: "last_seven_days" }],
  //   },
  // ) {
  //   params.metric_keys = SEGMENT_CUSTOM_METRIC_TYPES;
  //   let query = serializeQuery(params);
  //   if (query) query = `?${query}`;
  //   return apiFetch(
  //     `${ApiBaseUrl}apps/${appId}/segments/${segmentId}/custom_metrics${query}`,
  //   );
  // }
  static getAppDashboard(
    appId,
    params = {
      metric_periods: [{ period: "this_week" }],
    },
  ) {
    let query = serializeQuery(params);
    if (query) query = `?${query}`;
    return apiFetch(`${ApiBaseUrl}apps/${appId}/dashboard_metrics${query}`);
  }

  static getSegments(
    appId,
    params = {
      metric_periods: [{ period: "last_seven_to_fourteen_days" }, { period: "last_seven_days" }],
    },
  ) {
    let query = serializeQuery(params);
    if (query) query = `?${query}`;
    return apiFetch(`${ApiBaseUrl}apps/${appId}/segments/all_metrics${query}`);
  }

  static getOnePath(
    appId,
    pathId,
    params = {
      metric_periods: [{ period: "last_seven_to_fourteen_days" }, { period: "last_seven_days" }],
    },
  ) {
    let query = serializeQuery(params);
    if (query) query = `?${query}`;
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/${pathId}/metrics${query}`);
  }

  // Async metrics (RE-1459): backend may answer 202 with { async, status_key };
  // the key is derived server-side, so polling resumes after navigating away.
  static getMetricsStatus(appId, { pathId = null, statusKey }) {
    const base = pathId
      ? `${ApiBaseUrl}apps/${appId}/paths/${pathId}/metrics_status`
      : `${ApiBaseUrl}apps/${appId}/paths/metrics_status`;
    return apiFetch(`${base}?${serializeQuery({ status_key: statusKey })}`);
  }

  static async pollMetricsStatus(
    appId,
    {
      pathId = null,
      statusKey,
      intervalMs = 3000,
      maxIntervalMs = 15000,
      timeoutMs = 600000,
      onStatus,
    } = {},
  ) {
    const startTime = +new Date();
    let waitMs = intervalMs;
    for (;;) {
      const status = await ApiMetrics.getMetricsStatus(appId, { pathId, statusKey });
      if (typeof onStatus === "function") onStatus(status);
      if (status?.status === "PROCESSED") return status;
      if (status?.status === "FAILED") {
        // don't surface backend error details raw in user-facing toasts
        const warmFailed = new Error(IgnoredErrors.METRICS_WARM_FAILED.value);
        warmFailed.detail = status?.error;
        warmFailed.statusKey = statusKey;
        throw warmFailed;
      }
      if (+new Date() - startTime > timeoutMs) {
        const warming = new Error(IgnoredErrors.METRICS_WARMING.value);
        warming.statusKey = statusKey;
        throw warming;
      }
      await new Promise(resolve => {
        setTimeout(resolve, waitMs);
      });
      // exponential backoff caps at maxIntervalMs to spare the status endpoint
      waitMs = Math.min(waitMs * 1.5, maxIntervalMs);
    }
  }

  static getOnePathCustom(
    appId,
    pathId,
    params = {
      metric_periods: [{ period: "last_seven_to_fourteen_days" }, { period: "last_seven_days" }],
    },
  ) {
    params.metric_keys ??= [PATH_CUSTOM_METRIC_PIECHART];
    let query = serializeQuery(params);
    if (query) query = `?${query}`;
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/${pathId}/custom_metrics${query}`);
  }

  static getOnePathCohort(
    appId,
    pathId,
    params = {
      activity: "eligible",
      days: 30,
    },
  ) {
    let query = serializeQuery(params);
    if (query) query = `?${query}`;
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/${pathId}/cohort_metrics${query}`);
  }

  static getRetentions(
    appId,
    params = {
      metric_periods: [{ period: "last_seven_to_fourteen_days" }, { period: "last_seven_days" }],
    },
  ) {
    params.path_type =
      params.path_type ??
      "retention_modal,video,horizontal,vertical,tile,text,invisible,widget,interstitial,email,bottom_banner";
    const query = serializeQuery(params);
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/all_metrics${query ? `?${query}` : ""}`);
  }

  static getPathExperiments(
    appId,
    params = {
      metric_periods: [{ period: "last_seven_to_fourteen_days" }, { period: "last_seven_days" }],
    },
  ) {
    let query = serializeQuery(params);
    if (query) query = `&${query}`;
    return apiFetch(`${ApiBaseUrl}apps/${appId}/experiments/all_metrics?path_type=custom${query}`);
  }

  static getRetentionExperiments(
    appId,
    params = {
      metric_periods: [{ period: "last_seven_to_fourteen_days" }, { period: "last_seven_days" }],
      path_type:
        "retention_modal,video,horizontal,vertical,tile,text,invisible,widget,interstitial,email,bottom_banner",
    },
  ) {
    const query = serializeQuery(params);
    return apiFetch(
      `${ApiBaseUrl}apps/${appId}/experiments/all_metrics${query ? `?${query}` : query}`,
    );
  }

  static getOnePathActionGroups(
    appId,
    pathId,
    params = {
      metric_periods: [{ period: "last_seven_to_fourteen_days" }, { period: "last_seven_days" }],
    },
  ) {
    let query = serializeQuery(params);
    if (query) query = `?${query}`;
    return apiFetch(`${ApiBaseUrl}apps/${appId}/paths/${pathId}/action_group_metrics${query}`);
  }

  static getExperimentCustomMetrics(
    pathId,
    experimentId,
    params = {
      metric_periods: [{ period: "last_seven_days" }],
    },
  ) {
    params.metric_keys ??= [PATH_CUSTOM_METRIC_PIECHART];
    let query = serializeQuery(params);
    if (query) query = `?${query}`;
    return apiFetch(
      `${ApiBaseUrl}paths/${pathId}/experiments/${experimentId}/custom_metrics${query}`,
    );
  }

  static getSequences(
    appId,
    params = {
      metric_periods: [{ period: "last_seven_to_fourteen_days" }, { period: "last_seven_days" }],
    },
  ) {
    const query = serializeQuery(params);
    return apiFetch(`${ApiBaseUrl}apps/${appId}/sequences/all_metrics${query ? `?${query}` : ""}`);
  }

  static getTagsMetrics(
    appId,
    params = {
      metric_periods: [{ period: "last_seven_to_fourteen_days" }, { period: "last_seven_days" }],
    },
  ) {
    const query = serializeQuery(params);
    return apiFetch(`${ApiBaseUrl}apps/${appId}/tags/metrics${query ? `?${query}` : ""}`);
  }
}
